import React from "react";

import RankCategory from "../../components/RankCategory";
import "./index.css";

const Ranks = () => {
  // ========================================
  // RANKS PAGE - MAIN DATA STRUCTURE
  // ========================================
  // This is the main data structure for all rank categories and individual ranks
  //
  // TO ADD A NEW RANK CATEGORY:
  // 1. Add a new object to the rankCategories array below
  // 2. Include: title, description, and ranks array
  // 3. Each rank needs: icon, title, tag (optional), description
  //
  // TO ADD A NEW RANK TO EXISTING CATEGORY:
  // 1. Find the category in the rankCategories array
  // 2. Add a new rank object to the ranks array
  // 3. Include: icon, title, tag (optional), description
  //
  // TO CHANGE RANK INFORMATION:
  // 1. Find the rank in the appropriate category
  // 2. Update the icon, title, tag, or description
  // 3. Save the file to see changes
  //
  // TAG SYSTEM:
  // - Staff role: Orange background (#ebae46) - for command positions
  // - Veteran: Teal background (#8db1a1) - for experienced personnel
  // - Recruit: Gray background (#999999) - for new members
  // - No tag: Some ranks don't have tags (like Senior Non-commissioned Officers)

  // Rank categories data
  const rankCategories = [
    {
      title: "Regimental Command",
      description:
        "The Highest Level Of Leadership, Responsible For Overseeing The Entire Regiment",
      ranks: [
        {
           icon: <img src="/images/LTGEN.png" width="32" height="32" />,
          title: "[09] Lieutenant General",
          tag: { text: "CLOSED" },
          description:
            "Non-Operational Role At Current Stage",
        },
        {
           icon: <img src="/images/MAJGEN.png" width="32" height="16" />,
          title: "[08] Major General",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being The Commander In Chief/Server Owner",
        },
        {
           icon: <img src="/images/BGEN.png" width="32" height="32" />,
          title: "[07] Brigadier General",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being As The XO To The Major General",
        },
        {
          icon: <img src="/images/COL.png" width="32" height="18" />,
          title: "[06] Colonel",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being Selected As A Regimental Manager In A Specific Field"
        },
      ],
    },
    {
      title: "Battalion Command",
      description:
        "Second Highest Level Of Leadership. They Manage Operations Within The Staff Battalions, Ensuring Everything Runs Smoothly",
      ranks: [
        {
           icon: <img src="/images/LTCOL.png" width="32" height="32" />,
          title: "[05] Lieutenant Colonel",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being Chosen To Be A Staff Battalion CO/Operations Manager For A Staff Battalion",
        },
        {
           icon: <img src="/images/MAJ.png" width="32" height="32" />,
          title: "[04] Major",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being Chosen As A Staff Battalion XO/Operations Assistant For A Staff Battalion",
        },
      ],
    },
    {
      title: "Company Command",
      description:
        "The Third Highest Level Of Leadership. These Officers Are The Backbone Of Staff As They Ensure All Departments Run Smoothy",
      ranks: [
        {
          icon: <img src="/images/CPT.png" width="32" height="32" />,
          title: "[03] Captain",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being Chosen As A Head Of Junior Staff For A Staff Company Within A Staff Battalion. Usually A Department CO",
        },
        {
          icon: <img src="/images/FLT.png" width="20" height="40" />,
          title: "[02] 1st Lieutenant",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Showing Good Performance And Merit As Staff. Usually A Department XO",
        },
        {
          icon: <img src="/images/SLT.png" width="20" height="40" />,
          title: "[01] 2nd Lieutenant",
          tag: { text: "Staff Role" },
          description:
            "Acquired By Being Chosen As A Staff Member After Passing OCS",
        },
      ],
    },
    {
      title: "Warrant Officers",
      description:
        "Those Who Have Decided To Assist The Server With Moderation, To Ensure Its A Safe Environment For All",
      ranks: [
        {
          icon: <img src="/images/CWO5W.png" width="20" height="40" />,
          title: "[CW5] Chief Warrant Officer V",
          tag: { text: "Junior Staff ", className: "veteran" },
          description:
            "Acquired Being Chosen As A Junior Staff Member After Passing JOCS",
        },
        {
          icon: <img src="/images/CWO4W.png" width="20" height="40" />,
          title: "[CW4] Chief Warrant Officer IV",
          tag: { text: "Junior Staff ", className: "veteran" },
          description:
            "Acquired Being Chosen As A Junior Staff Member After Passing JOCS",
        },
        {
          icon: <img src="/images/CWO3W.png" width="20" height="40" />,
          title: "[CW3] Chief Warrant Officer III",
          tag: { text: "Junior Staff ", className: "veteran" },
          description:
            "Acquired Being Chosen As A Junior Staff Member After Passing JOCS",
        },
        {
          icon: <img src="/images/CWO2W.png" width="20" height="40" />,
          title: "[CW2] Chief Warrant Officer II",
          tag: { text: "Junior Staff ", className: "veteran" },
          description:
            "Acquired Being Chosen As A Junior Staff Member After Passing JOCS",
        },
        {
          icon: <img src="/images/WO1W.png" width="20" height="40" />,
          title: "[WO1] Warrant Officer I",
          tag: { text: "Junior Staff", className: "veteran" },
          description:
            "Acquired Being Chosen As A Junior Staff Member After Passing JOCS",
        },
      ],
    },
    {
      title: "Senior Non-Commissioned Officers",
      description:
        "For Those Who Have Shown Of Their Dedication Towards The Regiment",
      ranks: [
        {
          icon: <img src="/images/SGTMAJ.png" width="32" height="32" />,
          title: "[E9B] Sergeant Major",
          description: "Acquired By Achieving Level 20",
        },
        {
          icon: <img src="/images/MGYSGT.png" width="32" height="32" />,
          title: "[E9A] Master Gunnery Sergeant",
          description:
            "Acquired By Achieving Level 18",
        },
        {
          icon: <img src="/images/MSGT.png" width="32" height="32" />,
          title: "[E8B] Master Sergeant",
          description: "Acquired By Achieving Level 16",
        },
        {
          icon: <img src="/images/FSGT.png" width="32" height="32" />,
          title: "[E8A] First Sergeant",
          description:
            "Acquired By Achieving Level 14",
        },
        {
          icon: <img src="/images/GYSGT.png" width="32" height="32" />,
          title: "[E7] Sergeant First Class",
          description:
            "Acquired By Achieving Level 12",
        },
      ],
    },
    {
      title: "Non-Commissioned Officers",
      description:
        "For Those Who Have Shown Some Dedication Towards The Regiment",
      ranks: [
        {
          icon: <img src="/images/SSGT.png" width="32" height="32" />,
          title: "[E6] Staff Sergeant",
          description: "Acquired By Achieving Level 10",
        },
        {
          icon: <img src="/images/SGT.png" width="32" height="32" />,
          title: "[E5] Sergeant",
          description:
            "Acquired By Achieving Level 8",
        },
        {
          icon: <img src="/images/CPL.png" width="32" height="32" />,
          title: "[E4] Corporal",
          description:
            "Acquired By Achieving Level 6",
        },
      ],
    },
    {
      title: "Junior Enlisted",
      description: "For Those Who Have Just Joined. The Life And Soul Of The Regiment",
      ranks: [
        {
          icon: <img src="/images/LCPL.png" width="32" height="32" />,
          title: "[E3] Lance Corporal",
          description:
            "Acquired By Achieving Level 4",
        },
        {
          icon: <img src="/images/PFC.png" width="32" height="32" />,
          title: "[E2] Private First Class",
          description: "Acquired By Achieving Level 2",
        },
        {
          title: "[E1] Private",
          description:
            "Acquired By Joining The Server",
        },
      ],
    },
  ];

  // ========================================
  // PAGE LAYOUT - EDIT TITLES AND CONTENT HERE
  // ========================================
  // This section controls how the page looks and what content appears
  //
  // TO CHANGE THE PAGE TITLE:
  // - Change "Ranks" to whatever you want
  //
  // TO CHANGE THE INFO CARD:
  // - Update the image src, title, and text content below
  // - The image should be in the public/images/ folder
  //
  // TO CHANGE RANK CATEGORIES:
  // - Edit the rankCategories array above
  // - The categories will automatically display in the order they appear in the array

  return (
    <div className="ranks-page">
      <div className="ranks-container">
        {/* PAGE TITLE - Change this text to update the main heading */}
        <h1 className="ranks-title">Ranks</h1>

        {/* RANKS INFORMATION CARD - Edit this section to update the intro content */}
        <div className="ranks-info-card">
          <div className="ranks-card-image">
            {/* INFO CARD IMAGE - Change src to use a different image */}
            <img
              src="/images/battlefield6.jpg"
              alt="Military personnel in tactical gear"
              className="ranks-image"
            />
          </div>
          <div className="ranks-card-content">
            {/* INFO CARD TITLE - Change this text */}
            <h2 className="ranks-card-title">
              Ranks of the 5th Marine Regiment
            </h2>
            {/* INFO CARD TEXT - Change these paragraphs */}
            <p className="ranks-card-text">
              Ranks are the foundation of order, discipline, and progression
              within the 5th Marine Regiment. Every member, from the newest
              recruit to the highest-ranking officer, plays an important role in
              the unit's success. Advancement through the ranks reflects not
              only your dedication and time spent within the Regiment, but also
              your leadership, skill, and contribution to the community.
            </p>
            <p className="ranks-card-text">
              All ranks from E1 through E9B are earned through server levels,
              which can be gained by participating in voice chat and text chat.
              Higher officer and staff positions are assigned to those who
              demonstrate exceptional commitment and leadership within the
              Regiment.
            </p>
          </div>
        </div>

        {/* RANK CATEGORIES - Automatically displays all categories from rankCategories array */}
        <div className="ranks-categories">
          {rankCategories.map((category, index) => (
            <RankCategory
              key={index}
              title={category.title}
              description={category.description}
              ranks={category.ranks}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Ranks;
